import { Modal, Checkbox, Button, Select } from 'iglooform';
import { useEffect, useState } from 'react';
import { Row, Col } from 'antd';
import useMobile from '@/hooks/useMobile';
import { useIntl, FormattedMessage } from 'react-intl';

import specialIntlMessages from '@/utils/specialIntlMessages';

import './index.less';

interface Addon {
  add_on_id: number;
  enable: boolean;
  max_coverage?: string;
  name?: string;
  option: {
    coverage?: string;
    passenger_count?: number;
  };
  premium?: string;
  prerequisite?: number;
  required?: boolean;
}
interface IProps {
  addonList: Addon[];
  visible: boolean;
  onCancel: any;
  addonOptions: any;
  onConfirm: (addon: any) => any;
}

const AddOnList = (props: IProps) => {
  const { addonList, visible, onCancel, addonOptions, onConfirm } = props;
  const isMobile = useMobile();
  const { formatMessage } = useIntl();

  const [mustSelectAddons, setMustSelectAddons] = useState<number[]>([]);
  const [selectedAddon, setSelectedAddon] = useState<number[]>([]);
  const [canSelectAll, setCanSelectAll] = useState(true);
  const [cachedAddon, setCachedAddon] = useState<Addon[]>([]);

  useEffect(() => {
    const hasSelected = addonList
      .filter((add) => add.enable)
      .map((add) => add.add_on_id);
    setSelectedAddon(hasSelected);

    const mustSelect = addonList
      .filter((add) => add.required)
      .map((add) => add.add_on_id);
    setMustSelectAddons(mustSelect);
    setCachedAddon(JSON.parse(JSON.stringify(addonList)));
  }, [addonList, visible]);

  useEffect(() => {
    if (mustSelectAddons.length === selectedAddon.length) {
      setCanSelectAll(true);
    } else {
      setCanSelectAll(false);
    }
  }, [mustSelectAddons, selectedAddon]);

  useEffect(() => {
    const newList = cachedAddon.map((addon) => {
      const { add_on_id, prerequisite } = addon;
      const passRequire = prerequisite
        ? selectedAddon.includes(prerequisite)
        : true;
      return {
        ...addon,
        enable: selectedAddon.includes(add_on_id) && passRequire,
      };
    });
    setCachedAddon(newList);
  }, [selectedAddon]);

  return (
    <Modal
      destroyOnClose
      title={formatMessage({ id: 'Add-on Benefits' })}
      visible={visible}
      onCancel={onCancel}
      closable
      className="addonModal"
      footer={
        <Row justify="end" style={{ width: '100%' }}>
          <Col {...(isMobile ? { span: 24 } : {})}>
            <Button
              style={isMobile ? { width: '100%' } : {}}
              type="primary"
              onClick={() => {
                onConfirm(cachedAddon);
                onCancel();
              }}
            >
              {formatMessage({ id: 'Confirm' })}
            </Button>
          </Col>
        </Row>
      }
    >
      <div className="topButton">
        <Button
          type="link"
          onClick={() => {
            if (canSelectAll) {
              const newAddon = cachedAddon.map((add) => ({
                ...add,
                enable: true,
              }));
              setCachedAddon(newAddon);
              setCanSelectAll(false);
            } else {
              const newAddon = cachedAddon.map((add) => ({
                ...add,
                enable: add.required || false,
              }));
              setCachedAddon(newAddon);
              setCanSelectAll(true);
            }
          }}
        >
          {canSelectAll
            ? formatMessage({ id: 'Select All' })
            : formatMessage({ id: 'Clear Section' })}
        </Button>
      </div>

      {cachedAddon.map((addon, index) => {
        const {
          add_on_id,
          option = {},
          enable,
          required,
          prerequisite,
        } = addon;
        const needPre = prerequisite && !selectedAddon.includes(prerequisite);
        return (
          <div className="addon-box" key={add_on_id}>
            <div className="checkBox">
              <Checkbox
                checked={enable && !needPre}
                disabled={required || needPre}
                onChange={(e: any) => {
                  // addon.enable = e.target.checked;

                  const selectedIndex = selectedAddon.indexOf(add_on_id);
                  if (selectedIndex === -1) {
                    selectedAddon.push(add_on_id);
                    selectedAddon.length === cachedAddon.length &&
                      setCanSelectAll(true);
                  } else {
                    selectedAddon.splice(selectedIndex, 1);
                  }

                  setSelectedAddon([...selectedAddon]);

                  // setCachedAddon((old) => {
                  //   old[index] = addon;
                  //   return [...old];
                  // });
                }}
              />
              <span>
                <FormattedMessage {...specialIntlMessages[add_on_id]} />
              </span>
            </div>

            {(option.coverage || option.passenger_count) && (
              <div className={'divider'}></div>
            )}

            <Row gutter={10}>
              {option.coverage && (
                <Col
                  className="optionSelect"
                  span={option.passenger_count ? 12 : 24}
                >
                  <div>{formatMessage({ id: 'Coverage (Per Passenger)' })}</div>
                  <Select
                    defaultValue={option.coverage}
                    options={addonOptions[add_on_id]}
                    onChange={(e) => {
                      addon.option.coverage = String(e);

                      setCachedAddon((old) => {
                        old[index] = addon;
                        return [...old];
                      });
                    }}
                  ></Select>
                </Col>
              )}
              {option.passenger_count && (
                <Col className="optionSelect" span={option.coverage ? 12 : 24}>
                  <div>{formatMessage({ id: 'Number of Passengers' })}</div>
                  <Select
                    defaultValue={option.passenger_count}
                    options={Array(4)
                      .fill(1)
                      .map((_, index) => ({
                        label: index + 1,
                        value: index + 1,
                      }))}
                    onChange={(e) => {
                      addon.option.passenger_count = Number(e);
                      setCachedAddon((old) => {
                        old[index] = addon;
                        return [...old];
                      });
                    }}
                  ></Select>
                </Col>
              )}
            </Row>
          </div>
        );
      })}
    </Modal>
  );
};

export default AddOnList;
